/* =========================================================================
   SÈVE — SHARED MOTION ENGINE
   Owns scroll reveals for server-rendered .sl-rev / [data-animate] elements,
   exclusive accordion groups, stat roll-ups and the small utility hooks the
   sections call (slMotion.stagger / atcDone / pulse).

   Contract: the hidden state only exists under html.js-anim, which this file
   adds. Whatever the observers do, everything is force-revealed 2.5s after
   DOMContentLoaded, on pagehide, and when the tab is backgrounded. A missed
   animation always beats hidden content.
   ========================================================================= */
(function () {
  var RM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var M = window.slMotion = window.slMotion || {};

  function markDone(el) {
    if (el.classList.contains('sl-rev')) el.classList.add('is-in');
    if (el.hasAttribute && el.hasAttribute('data-animate')) el.classList.add('revealed');
  }
  function isDone(el) {
    if (el.classList.contains('sl-rev') && !el.classList.contains('is-in')) return false;
    if (el.hasAttribute('data-animate') && !el.classList.contains('revealed')) return false;
    return true;
  }
  function revealAll() {
    document.querySelectorAll('.sl-rev:not(.is-in)').forEach(function (el) {
      el.style.transitionDelay = '0ms';
      el.classList.add('is-in');
    });
    document.querySelectorAll('[data-animate]:not(.revealed)').forEach(function (el) {
      el.style.transitionDelay = '0ms';
      el.classList.add('revealed');
    });
  }

  /* re-stagger a container's children (planner timeline etc.) */
  M.stagger = function (box, opts) {
    if (!box || RM) return;
    var cls = opts && opts.fade ? 'sl-stg-fade' : 'sl-stg-item';
    var kids = [].slice.call(box.children);
    kids.forEach(function (k, i) {
      k.classList.remove('is-in');
      k.classList.add(cls);
      k.style.transitionDelay = Math.min(i, 4) * 60 + 'ms';
    });
    void box.offsetWidth;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { kids.forEach(function (k) { k.classList.add('is-in'); }); });
    });
    setTimeout(function () { kids.forEach(function (k) { k.classList.add('is-in'); }); }, 800);
  };

  /* add-to-cart button morphs to a checkmark for 1.2s */
  M.atcDone = function (btn) {
    if (!btn) return;
    var span = btn.querySelector('span') || btn;
    if (btn.dataset.slOrig == null) btn.dataset.slOrig = span.textContent;
    var fr = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
    btn.classList.add('is-added');
    span.textContent = fr ? 'Ajouté ✓' : 'Added ✓';
    clearTimeout(btn._slAdd);
    btn._slAdd = setTimeout(function () {
      btn.classList.remove('is-added');
      span.textContent = btn.dataset.slOrig;
    }, 1200);
  };

  /* one spring scale tick */
  M.pulse = function (el) {
    if (!el || RM) return;
    el.classList.remove('sl-pulse');
    void el.offsetWidth;
    el.classList.add('sl-pulse');
  };

  /* stat roll-up: starts at 62% of the real (already rendered) value and
     lands in ~550ms, so the number is never misleading. Digits+unit only —
     ratios like 1:1 are left alone. */
  function rollup(el) {
    var m = /^(\d+(?:\.\d+)?)([a-z%]*)$/i.exec(el.textContent.trim());
    if (!m) return;
    var end = parseFloat(m[1]);
    var dec = (m[1].split('.')[1] || '').length;
    var suf = m[2], t0 = null, D = 550, start = end * 0.62;
    function step(ts) {
      if (!t0) t0 = ts;
      var p = Math.min(1, (ts - t0) / D);
      p = 1 - Math.pow(1 - p, 3);
      el.textContent = (start + (end - start) * p).toFixed(dec) + suf;
      if (p < 1) requestAnimationFrame(step); else el.textContent = m[1] + suf;
    }
    requestAnimationFrame(step);
  }

  /* opening one accordion closes its group siblings. Runs at document level
     AFTER each section's own toggle handler, so it composes with all of them. */
  function exclusiveAccordions() {
    document.addEventListener('click', function (ev) {
      var head = ev.target.closest('.sl-acc__head');
      if (!head) return;
      var item = head.closest('.sl-acc__item');
      if (!item) return;
      var group = head.closest('[data-accordion-group]') || item.parentElement;
      setTimeout(function () {
        if (!item.classList.contains('is-open')) return;
        [].forEach.call(group.querySelectorAll('.sl-acc__item.is-open'), function (sib) {
          if (sib === item) return;
          sib.classList.remove('is-open');
          var h = sib.querySelector('.sl-acc__head');
          if (h) h.setAttribute('aria-expanded', 'false');
        });
        setTimeout(function () {
          item.scrollIntoView({ block: 'nearest', behavior: RM ? 'auto' : 'smooth' });
        }, 380);
      }, 0);
    });
  }

  function statRollups() {
    if (RM || !('IntersectionObserver' in window)) return;
    var els = [].slice.call(document.querySelectorAll('.sl-stat__n, .slp-key__n'));
    if (!els.length) return;
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        rollup(e.target);
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  function init() {
    if (document.documentElement.dataset.slMotionInit) return;
    document.documentElement.dataset.slMotionInit = '1';

    exclusiveAccordions();

    if (RM || !('IntersectionObserver' in window)) return; /* everything stays visible */
    document.documentElement.classList.add('js-anim');

    statRollups();

    var els = [].slice.call(document.querySelectorAll('.sl-rev, [data-animate]'));
    if (!els.length) return;

    /* sibling stagger: 60ms steps, capped at 5 staggered items per group */
    var counts = new Map();
    els.forEach(function (el) {
      var p = el.parentElement;
      var i = counts.get(p) || 0;
      counts.set(p, i + 1);
      el.style.transitionDelay = Math.min(i, 3) * 100 + 'ms';
    });

    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        markDone(e.target);
        io.unobserve(e.target);
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -5% 0px' });

    els.forEach(function (el) {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.95) {
        el.style.transitionDelay = '0ms';
        markDone(el);
      } else {
        io.observe(el);
      }
    });

    /* sweep on jump-scrolls, then the unconditional 2.5s failsafe */
    var sweep = function () {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        if (isDone(el)) return;
        if (el.getBoundingClientRect().top < vh) {
          el.style.transitionDelay = '0ms';
          markDone(el);
        }
      });
    };
    window.addEventListener('scroll', function () { setTimeout(sweep, 120); }, { passive: true });
    window.addEventListener('resize', sweep, { passive: true });
    window.addEventListener('load', sweep);
    setTimeout(sweep, 1200);
    setTimeout(revealAll, 2500);
    window.addEventListener('pagehide', revealAll);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') revealAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
